/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';

import CommunicatorUtility from '../utility/communicator-utility';

/**
 * @private
 * @typedef {import('../../control/control-exceptions').ControlExceptions} ControlExceptions
 * @typedef {import('../../control/control-lock').ControlLock} ControlLock
 * @typedef {import('../settings/communicator-settings').CommunicatorSettingsGetter} CommunicatorSettingsGetter
 */

/**
 * Komunikátor nastavení ovládání zařízení pro Ki-Wi Server.
 */
class CommunicatorControl {
  /**
   * Načte z nastavení výjimky z ovládání.
   * @param {boolean | null} power Příznak z nastavení serveru.
   * @private
   * @returns {ControlExceptions | null} Vrátí `null`, pokud žádné výjimky nejsou.
   */
  static parseExceptions(power) {
    if (!power) {
      return null;
    }
    return { power };
  }

  /**
   * Načte z nastavení zámek ovládání pro tlačítka umístěné přímo na zařízení.
   * @param {CommunicatorSettingsGetter} settings Nastavení ze serveru.
   * @returns {ControlLock | null} Vrátí `null`, pokud se zámek nemá nastavit.
   */
  static parseLocalLock(settings) {
    return CommunicatorControl.parseLock(
      settings.get('control.local.locked'),
      settings.get('control.local.exceptions.power')
    );
  }

  /**
   * Načte z nastavení obecný zámek ovládání.
   * @param {string | null | undefined} lockedString Příznak jako řetězec z nastavení serveru.
   * @param {string | null | undefined} powerString Příznak jako řetězec z nastavení serveru.
   * @private
   * @returns {ControlLock | null} Vrátí `null`, pokud se zámek nemá nastavit.
   */
  static parseLock(lockedString, powerString) {
    const lockedBoolean = CommunicatorUtility.parseBoolean(lockedString);
    const powerBoolean = CommunicatorUtility.parseBoolean(powerString);
    const locked = CommunicatorControl.parseLocked(lockedBoolean, powerBoolean);
    const exceptions = CommunicatorControl.parseExceptions(powerBoolean);
    if (isNil(locked)) {
      return null;
    }
    if (isNil(exceptions)) {
      return { locked };
    }
    return { exceptions, locked };
  }

  /**
   * Načte z nastavení příznak jestli se má zámek ovládání zamknout.
   * @param {boolean | null} locked Příznak z nastavení serveru.
   * @param {boolean | null} power Příznak z nastavení serveru.
   * @private
   * @returns {boolean | null} Příznak pro zámek ovládání.
   * Vrátí `null`, pokud se zámek nemá nastavit.
   */
  static parseLocked(locked, power) {
    if (isNil(locked) && isNil(power)) {
      return null;
    }
    if (isNil(locked)) {
      return false;
    }
    return locked;
  }

  /**
   * Načte z nastavení zámek ovládání pro tlačítka na dálkovém ovládání.
   * @param {CommunicatorSettingsGetter} settings Nastavení ze serveru.
   * @returns {ControlLock | null} Vrátí `null`, pokud se zámek nemá nastavit.
   */
  static parseRemoteLock(settings) {
    return CommunicatorControl.parseLock(
      settings.get('control.remote.locked'),
      settings.get('control.remote.exceptions.power')
    );
  }
}

export default CommunicatorControl;
